import { useState } from 'react'
import { motion } from 'framer-motion'
import { Bell, ChevronDown, ChevronUp, Save, AlertCircle, CheckCircle, Smartphone, Mail, MessageSquare, Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'
import {
  ensurePushNotificationsReady,
  getPushSupportError,
  isPushNotificationsSupported,
} from '@/lib/pushNotifications'

interface NotificationSectionProps {
  title: string
  subtitle: string
}

export function NotificationSection({ title, subtitle }: NotificationSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    // Push notifications
    push_notifications_enabled: false,
    push_transaction_notifications: false,
    push_security_notifications: false,
    push_marketing_notifications: false,
    
    // Email notifications
    email_notifications_enabled: false,
    email_transaction_notifications: false,
    email_security_notifications: false,
    email_marketing_notifications: false,
    
    // SMS notifications
    sms_notifications_enabled: false,
    sms_transaction_notifications: false,
    sms_security_notifications: false,
    
    // Quiet hours
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00'
  })
  
  const { t } = useTranslation()
  const pushSupported = isPushNotificationsSupported()

  // Load notification settings when expanding
  const handleExpand = async () => {
    if (!isExpanded) {
      setIsLoading(true)
      setError(null)
      setSuccess(null)
      
      try {
        // Load current notification settings from API
        const response = await fetch('/api/notification-settings/', {
          credentials: 'include'
        })
        const data = await response.json()
        
        if (data.results && data.results.length > 0) {
          const settings = data.results[0]
          setNotificationSettings({
            push_notifications_enabled: settings.push_notifications_enabled,
            push_transaction_notifications: settings.push_transaction_notifications,
            push_security_notifications: settings.push_security_notifications,
            push_marketing_notifications: settings.push_marketing_notifications,
            email_notifications_enabled: settings.email_notifications_enabled,
            email_transaction_notifications: settings.email_transaction_notifications,
            email_security_notifications: settings.email_security_notifications,
            email_marketing_notifications: settings.email_marketing_notifications,
            sms_notifications_enabled: settings.sms_notifications_enabled,
            sms_transaction_notifications: settings.sms_transaction_notifications,
            sms_security_notifications: settings.sms_security_notifications,
            quiet_hours_enabled: settings.quiet_hours_enabled,
            quiet_hours_start: settings.quiet_hours_start || '22:00',
            quiet_hours_end: settings.quiet_hours_end || '08:00'
          })
        }
      } catch (error: any) {
        console.error('Error loading notification settings:', error)
        setError(t('settings.notificationLoadError', 'Failed to load notification settings'))
      } finally {
        setIsLoading(false)
      }
    }
    setIsExpanded(!isExpanded)
  }

  // Handle setting toggle
  const handleSettingToggle = async (setting: string) => {
    if (setting === 'push_notifications_enabled' && !notificationSettings.push_notifications_enabled) {
      const supportError = getPushSupportError()

      if (supportError === 'secure_context') {
        setError(t('settings.pushPromptSecureContext', 'Push notifications require a secure HTTPS connection.'))
        return
      }

      if (supportError === 'config_missing') {
        setError(t('settings.pushPromptConfigMissing', 'Push notifications are not configured for this environment.'))
        return
      }

      if (!pushSupported || supportError) {
        setError(t('settings.pushPromptUnsupported', 'Push notifications are not supported in this browser.'))
        return
      }

      try {
        const { permission } = await ensurePushNotificationsReady()
        if (permission !== 'granted') {
          setError(
            permission === 'denied'
              ? t('settings.pushPromptDenied', 'Notification permission is blocked. Enable it in your browser settings.')
              : t('settings.pushPromptPermissionRequired', 'Allow browser notifications to enable push updates.')
          )
          return
        }
      } catch (error) {
        console.error('Error enabling browser notifications:', error)
        const message =
          error instanceof Error && error.message === 'vapid_missing'
            ? t('settings.pushPromptConfigMissing', 'Push notifications are not configured for this environment.')
            : t('settings.pushPromptEnableError', 'Unable to enable push notifications.')
        setError(message)
        return
      }
    }

    setNotificationSettings(prev => ({
      ...prev,
      [setting]: !prev[setting as keyof typeof prev]
    }))
    if (error) setError(null)
    if (success) setSuccess(null)
  }

  // Handle time change
  const handleTimeChange = (setting: string, value: string) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: value
    }))
    if (error) setError(null)
    if (success) setSuccess(null)
  }

  // Save notification settings
  const handleSave = async () => {
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await fetch('/api/notification-settings/update_settings/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(notificationSettings)
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess(t('settings.notificationUpdateSuccess', 'Notification settings updated successfully!'))
        toast.success(t('settings.notificationUpdateSuccess', 'Notification settings updated successfully!'))
      } else {
        setError(data.error || t('settings.notificationUpdateError', 'Failed to update notification settings'))
      }
    } catch (err: any) {
      console.error('Error updating notification settings:', err)
      setError(t('settings.notificationUpdateError', 'Failed to update notification settings'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <motion.button
        onClick={handleExpand}
        className="w-full flex items-center space-x-3 p-4 hover:bg-gray-50 transition-colors"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Bell className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 text-left">
          <h3 className="font-medium text-text-primary">{title}</h3>
          <p className="text-sm text-text-secondary">{subtitle}</p>
        </div>
        <div className="text-text-secondary">
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          ) : isExpanded ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </div>
      </motion.button>
      
      {/* Notification Settings Dropdown */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-gray-100 bg-gray-50"
        >
          <div className="p-4 space-y-6">
            {/* Success Message */}
            {success && (
              <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Push Notifications */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary flex items-center space-x-2">
                <Smartphone className="h-4 w-4" />
                <span>{t('settings.pushNotifications', 'Push Notifications')}</span>
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div>
                    <h5 className="font-medium text-text-primary">{t('settings.pushNotifications', 'Push Notifications')}</h5>
                    <p className="text-sm text-text-secondary">{t('settings.pushNotificationsDesc', 'Receive notifications on your device')}</p>
                  </div>
                  <button
                    onClick={() => handleSettingToggle('push_notifications_enabled')}
                    disabled={isLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      notificationSettings.push_notifications_enabled ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        notificationSettings.push_notifications_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {!pushSupported && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    {t('settings.pushPromptUnsupported', 'Push notifications are not supported in this browser.')}
                  </div>
                )}

                {notificationSettings.push_notifications_enabled && (
                  <div className="ml-4 space-y-2">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-text-primary">{t('settings.transactionNotifications', 'Transaction Notifications')}</span>
                      <button
                        onClick={() => handleSettingToggle('push_transaction_notifications')}
                        disabled={isLoading}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          notificationSettings.push_transaction_notifications ? 'bg-primary' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            notificationSettings.push_transaction_notifications ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-text-primary">{t('settings.securityNotifications', 'Security Notifications')}</span>
                      <button
                        onClick={() => handleSettingToggle('push_security_notifications')}
                        disabled={isLoading}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          notificationSettings.push_security_notifications ? 'bg-primary' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            notificationSettings.push_security_notifications ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-text-primary">{t('settings.marketingNotifications', 'Marketing Notifications')}</span>
                      <button
                        onClick={() => handleSettingToggle('push_marketing_notifications')}
                        disabled={isLoading}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          notificationSettings.push_marketing_notifications ? 'bg-primary' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            notificationSettings.push_marketing_notifications ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Email Notifications */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <span>{t('settings.emailNotifications', 'Email Notifications')}</span>
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div>
                    <h5 className="font-medium text-text-primary">{t('settings.emailNotifications', 'Email Notifications')}</h5>
                    <p className="text-sm text-text-secondary">{t('settings.emailNotificationsDesc', 'Receive notifications via email')}</p>
                  </div>
                  <button
                    onClick={() => handleSettingToggle('email_notifications_enabled')}
                    disabled={isLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      notificationSettings.email_notifications_enabled ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        notificationSettings.email_notifications_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {notificationSettings.email_notifications_enabled && (
                  <div className="ml-4 space-y-2">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-text-primary">{t('settings.transactionNotifications', 'Transaction Notifications')}</span>
                      <button
                        onClick={() => handleSettingToggle('email_transaction_notifications')}
                        disabled={isLoading}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          notificationSettings.email_transaction_notifications ? 'bg-primary' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            notificationSettings.email_transaction_notifications ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-text-primary">{t('settings.securityNotifications', 'Security Notifications')}</span>
                      <button
                        onClick={() => handleSettingToggle('email_security_notifications')}
                        disabled={isLoading}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          notificationSettings.email_security_notifications ? 'bg-primary' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            notificationSettings.email_security_notifications ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-text-primary">{t('settings.marketingNotifications', 'Marketing Notifications')}</span>
                      <button
                        onClick={() => handleSettingToggle('email_marketing_notifications')}
                        disabled={isLoading}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          notificationSettings.email_marketing_notifications ? 'bg-primary' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            notificationSettings.email_marketing_notifications ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SMS Notifications */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary flex items-center space-x-2">
                <MessageSquare className="h-4 w-4" />
                <span>{t('settings.smsNotifications', 'SMS Notifications')}</span>
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div>
                    <h5 className="font-medium text-text-primary">{t('settings.smsNotifications', 'SMS Notifications')}</h5>
                    <p className="text-sm text-text-secondary">{t('settings.smsNotificationsDesc', 'Receive notifications via SMS')}</p>
                  </div>
                  <button
                    onClick={() => handleSettingToggle('sms_notifications_enabled')}
                    disabled={isLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      notificationSettings.sms_notifications_enabled ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        notificationSettings.sms_notifications_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {notificationSettings.sms_notifications_enabled && (
                  <div className="ml-4 space-y-2">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-text-primary">{t('settings.transactionNotifications', 'Transaction Notifications')}</span>
                      <button
                        onClick={() => handleSettingToggle('sms_transaction_notifications')}
                        disabled={isLoading}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          notificationSettings.sms_transaction_notifications ? 'bg-primary' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            notificationSettings.sms_transaction_notifications ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-text-primary">{t('settings.securityNotifications', 'Security Notifications')}</span>
                      <button
                        onClick={() => handleSettingToggle('sms_security_notifications')}
                        disabled={isLoading}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          notificationSettings.sms_security_notifications ? 'bg-primary' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            notificationSettings.sms_security_notifications ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quiet Hours */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>{t('settings.quietHours', 'Quiet Hours')}</span>
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div>
                    <h5 className="font-medium text-text-primary">{t('settings.quietHours', 'Quiet Hours')}</h5>
                    <p className="text-sm text-text-secondary">{t('settings.quietHoursDesc', 'Disable notifications during specific hours')}</p>
                  </div>
                  <button
                    onClick={() => handleSettingToggle('quiet_hours_enabled')}
                    disabled={isLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      notificationSettings.quiet_hours_enabled ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        notificationSettings.quiet_hours_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {notificationSettings.quiet_hours_enabled && (
                  <div className="ml-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-text-primary">{t('settings.quietHoursStart', 'Start Time')}</label>
                        <input
                          type="time"
                          value={notificationSettings.quiet_hours_start}
                          onChange={(e) => handleTimeChange('quiet_hours_start', e.target.value)}
                          disabled={isLoading}
                          className="w-full mt-1 p-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-text-primary">{t('settings.quietHoursEnd', 'End Time')}</label>
                        <input
                          type="time"
                          value={notificationSettings.quiet_hours_end}
                          onChange={(e) => handleTimeChange('quiet_hours_end', e.target.value)}
                          disabled={isLoading}
                          className="w-full mt-1 p-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSave}
                disabled={isLoading}
                className="px-6"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t('common.saving', 'Saving...')}</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Save className="h-4 w-4" />
                    <span>{t('common.save', 'Save')}</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  )
}
